/**
 * ConfirmModal — the small shared confirm dialog (new code must not use
 * window.confirm). Overlay click + Escape close; body scroll is locked while
 * open. `danger` switches the confirm button to the destructive style;
 * `loading` disables both buttons while the confirm action is in flight.
 */
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { primaryBtnCls, secondaryBtnCls, dangerBtnCls } from './styles';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** One quiet explanatory line (or richer node) under the title. */
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive confirm (red fill). */
  danger?: boolean;
  /** Confirm action in flight — disables both buttons and spins the confirm. */
  loading?: boolean;
}

export default function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  loading = false,
}: ConfirmModalProps) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="my-8 w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800"
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        {message && <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">{message}</div>}
        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {cancelLabel}
          </button>
          <button type="button" onClick={onConfirm} disabled={loading} className={danger ? dangerBtnCls : primaryBtnCls}>
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
